import { PaletteMode } from "@mui/material";
import { AppData } from "store/appDataSlice";
import { AppColor } from "store/generalSettingsSlice";
import Storage from "./Storage";

enum Locals {
  ALLOWED_EXTERNAL_LINKS = "allowed-external-links",
  APP_DATA = "app-data",
  CHANGELOG_VERSION = "changelog-version",
  EXTERNAL_LINK_NAVIGATION = "external-link-navigation",
  GOOGLE_CALENDAR = "google-calendar",
  LAST_SEEN_ANNOUNCEMENT = "last-seen-announcement",
  PALETTE_MODE = "palette-mode",
  PRIMARY_COLOR = "primary-color",
  SEASONAL_THEME = "seasonal-theme",
  SHOW_INSTALL_PWA_BANNER = "show-install-pwa-banner",
  NOTIFICATION_DIALOG_SHOWN = "notification-dialog-shown",
}

export default class LocalStorage extends Storage<Locals> {
  private static instance?: LocalStorage;

  private constructor() {
    super();
  }

  public static getInstance(): LocalStorage {
    if (!this.instance) {
      this.instance = new LocalStorage();
    }

    return this.instance;
  }

  public getChangelogVersion(): string | null {
    return this.get(Locals.CHANGELOG_VERSION);
  }

  public setChangelogVersion(changelogVersion: string): void {
    this.set(Locals.CHANGELOG_VERSION, changelogVersion);
  }

  public getPaletteMode(): PaletteMode {
    const paletteMode = this.get(Locals.PALETTE_MODE) as PaletteMode | null;

    return paletteMode ?? "dark";
  }

  public setPaletteMode(paletteMode: PaletteMode): void {
    this.set(Locals.PALETTE_MODE, paletteMode);
  }

  public getPrimaryColor(): AppColor {
    const primaryColor = this.get(Locals.PRIMARY_COLOR) as AppColor | null;

    return primaryColor ?? "teal";
  }

  public setPrimaryColor(primaryColor: AppColor): void {
    this.set(Locals.PRIMARY_COLOR, primaryColor);
  }

  public getAllowedExternalLinks(): Array<string> {
    const externalLinks = this.get(Locals.ALLOWED_EXTERNAL_LINKS) ?? "[]";

    return JSON.parse(externalLinks);
  }

  public setAllowedExternalLinks(link: string): void {
    const externalLinks = JSON.parse(this.get(Locals.ALLOWED_EXTERNAL_LINKS) ?? "[]") as Array<string>;

    if (!externalLinks.includes(link)) {
      // if there are more then 10 links remove the first one added to save local storage space
      if (externalLinks.length >= 10) {
        externalLinks.shift();
        externalLinks.push(link);
      } else {
        externalLinks.push(link);
      }

      this.set(Locals.ALLOWED_EXTERNAL_LINKS, JSON.stringify(externalLinks));
    }
  }

  public getConfirmExternalLinkNavigation(): boolean {
    const externalLinkNavigation = this.get(Locals.EXTERNAL_LINK_NAVIGATION) as "true" | "false" | null;

    if (externalLinkNavigation === "false") {
      return false;
    }

    return true;
  }

  public setConfirmExternalLinkNavigation(externalLinkNavigation: boolean): void {
    this.set(Locals.EXTERNAL_LINK_NAVIGATION, JSON.stringify(externalLinkNavigation));
  }

  public getGoogleCalendarEvent(): boolean {
    const googleCalendar = this.get(Locals.GOOGLE_CALENDAR) as "true" | "false" | null;

    if (googleCalendar === "false") {
      return false;
    }

    return true;
  }

  public setGoogleCalendarEvent(googleCalendar: boolean): void {
    this.set(Locals.GOOGLE_CALENDAR, JSON.stringify(googleCalendar));
  }

  public getAppData(): AppData | null {
    const appData = this.get(Locals.APP_DATA);

    return appData ? (JSON.parse(appData) as AppData) : null;
  }

  public setAppData(appData: AppData | null): void {
    this.set(Locals.APP_DATA, JSON.stringify(appData));
  }

  public getLastSeenAnnouncementDate(): number | null {
    const lastSeeAnnouncementDate = this.get(Locals.LAST_SEEN_ANNOUNCEMENT);

    return lastSeeAnnouncementDate ? parseInt(lastSeeAnnouncementDate) : null;
  }

  public setLastSeenAnnouncementDate(lastSeeAnnouncementDate: number): void {
    this.set(Locals.LAST_SEEN_ANNOUNCEMENT, JSON.stringify(lastSeeAnnouncementDate));
  }

  public getNotificationDialogShown(): boolean {
    const notificationShown = this.get(Locals.NOTIFICATION_DIALOG_SHOWN) as "true" | "false" | null;

    if (notificationShown === "true") {
      return true;
    }

    return false;
  }

  public setNotificationDialogShown(notificationShown: boolean): void {
    this.set(Locals.NOTIFICATION_DIALOG_SHOWN, JSON.stringify(notificationShown));
  }
}
